/**
 * A FOLDER IN THE INBOX IS A ROW SOMEBODY SAVED, NOT A LINE OF CODE.
 *
 * The Product Owner, 2026-10-11, after going through Enso Connect's inbox:
 * 「我們目前再怎麼去寫業務邏輯也寫不完，只有用這個方法，我們才可以把所有的邏輯用自己去 Filter
 *  去創造，然後去把它涵蓋掉」.
 *
 * Reply, Follow up, Reminders, Current guests are code — each of them a pull request. The ways of
 * looking at a hundred conversations do not end there: only Zillow, only the Dravus suites, the
 * renters an investor sent, the ones whose move-in has already passed. This file is the other half:
 * the folders a person makes, kept in `mtr.inbox_folders` (migration 0087).
 *
 * WHAT IS HERE AND WHAT IS NOT. This is the shape of a saved folder and the rules about it that
 * hold wherever it is read — what a name may be, which colour, whose it is, what order they sit in.
 * What its CONDITIONS MEAN is `src/inbox/folderConditions.js`, deliberately a separate file: that
 * one reaches the Automations catalogue and through it the whole data layer, and the Inbox screen
 * imports this file. The asker is handed in; a folder with no conditions is every conversation.
 */
import { upgradeFilters } from '../automation/filterUpgrade.js'

/** The rail's palette, by name. A stored `#ff00ff` would outlive the screen that chose it. */
export const FOLDER_COLOURS = Object.freeze(['ink', 'red', 'amber', 'teal', 'blue', 'violet'])

/** Eight to choose from, as Enso offers — a rail is read by shape, and thirty shapes is noise. */
export const FOLDER_ICONS = Object.freeze(['📁', '💬', '⏰', '🔔', '🏠', '📞', '🏷', '♻️'])

export const NAME_MAX = 40
export const NOTE_MAX = 200
/*
  TWELVE CONDITIONS, AND THE LIMIT IS REAL. Intercom's own documentation warns that a view with five
  or more complex filters starts timing out, and every condition here is asked of every conversation
  on every render. The database refuses a thirteenth (0087); this says the same number out loud.
*/
const CONDITIONS_MAX = 12

const text = (v, max) => {
  const s = typeof v === 'string' ? v.trim() : ''
  return s ? s.slice(0, max) : null
}

/**
 * One row from `mtr.inbox_folders` as the screen wants it.
 *
 * `shared` is the question the rail asks — the team's heading or mine. There is no `mine` flag: it
 * would be `owner === me`, and the only row that reaches a person which is neither the team's nor
 * their own is one the database should not have handed over. Those are deliberately NOT dropped
 * here (see `foldersFrom`), so an RLS mistake is visible rather than quietly swallowed.
 */
/* Private, like `folderOrder`: `foldersFrom` is how the product reads these rows, and an export
   only a test imports is a lie about who uses it. */
function folderFromRow(row, { me = null } = {}) {
  if (!row?.id) return null
  const owner = row.owner ?? null
  return {
    id: row.id,
    name: text(row.name, NAME_MAX) ?? 'Folder',
    note: text(row.note, NOTE_MAX),
    icon: typeof row.icon === 'string' && row.icon.trim() ? row.icon.trim().slice(0, 8) : '📁',
    colour: FOLDER_COLOURS.includes(row.colour) ? row.colour : 'ink',
    /*
      READ AS WHAT THEY MEANT, like a saved Automation (src/data/map.js) and a stored Today rule
      (src/today/todayRules.js). #554 step C merged `New` and `In conversation` into `Inquiry`:
      no folder on production named either, but a folder that did would otherwise quietly match
      nothing from the moment the rows moved — and an empty folder looks like an empty inbox.
    */
    conditions: Array.isArray(row.conditions) ? upgradeFilters(row.conditions).slice(0, CONDITIONS_MAX) : [],
    newestFirst: row.newest_first !== false,
    owner,
    shared: owner == null,
    /* Whose it is, when it is not the team's — `null` for a row somebody else's name is on. */
    isMine: owner != null && me != null && owner === me,
    position: Number.isFinite(Number(row.position)) ? Number(row.position) : 100,
    createdAt: row.created_at ?? null,
  }
}

/** What goes back to the database. `owner` null is the whole team's — see 0087. */
export function folderToRow(f, { me = null } = {}) {
  return {
    ...(f.id ? { id: f.id } : {}),
    name: text(f.name, NAME_MAX) ?? 'Folder',
    note: text(f.note, NOTE_MAX),
    icon: typeof f.icon === 'string' && f.icon.trim() ? f.icon.trim().slice(0, 8) : '📁',
    colour: FOLDER_COLOURS.includes(f.colour) ? f.colour : 'ink',
    conditions: Array.isArray(f.conditions) ? f.conditions.slice(0, CONDITIONS_MAX) : [],
    newest_first: f.newestFirst !== false,
    owner: f.shared ? null : (f.owner ?? me ?? null),
    position: Number.isFinite(Number(f.position)) ? Math.max(0, Math.min(10000, Math.round(Number(f.position)))) : 100,
  }
}

/**
 * THE TEAM'S FIRST, THEN MINE, each in the order somebody put them in — and a tie broken by when it
 * was made, so the rail does not reshuffle itself between two renders.
 */
/* Private on purpose: `foldersFrom` is the only caller, and an export only a test reads is a
   lie about who uses it (the "Somebody calls it" gate, and #473's lesson about exported constants). */
function folderOrder(a, b) {
  if (a.shared !== b.shared) return a.shared ? -1 : 1
  if (a.position !== b.position) return a.position - b.position
  const ta = Date.parse(a.createdAt ?? '') || 0
  const tb = Date.parse(b.createdAt ?? '') || 0
  if (ta !== tb) return ta - tb
  return String(a.id).localeCompare(String(b.id))
}

/** Every saved folder this person should see, in rail order. */
export function foldersFrom(rows = [], { me = null } = {}) {
  return rows.map((r) => folderFromRow(r, { me })).filter(Boolean).sort(folderOrder)
}

/**
 * IS THIS CONVERSATION IN THIS FOLDER?
 *
 * A folder with no conditions is every conversation — which is what "All conversations" is, and
 * what a folder someone has just made and not finished is. The conditions themselves land in the
 * next change; until then a folder is a name and a place in the rail, and this says so rather than
 * pretending to filter.
 */
export function inSavedFolder(folder, lead, { ask = null } = {}) {
  if (!folder || !lead) return false
  const conditions = folder.conditions ?? []
  if (conditions.length === 0) return true
  /*
    NO ASKER MEANS NO ANSWER, AND NO ANSWER IS NO. A folder with conditions, rendered somewhere
    that cannot evaluate them, must not quietly behave like a folder with none — that is how
    "only Zillow" comes to show everybody.

    `ask` is handed in rather than imported (`src/inbox/folderConditions.js`), because the
    Automations catalogue reaches the whole world-reader and this file is the Inbox's own shape.
  */
  return typeof ask === 'function' ? Boolean(ask(conditions, lead)) : false
}

/** How many conversations each saved folder holds, by id. */
export function savedFolderCounts(folders = [], leads = [], asked = {}) {
  return Object.fromEntries(folders.map((f) => [f.id, leads.filter((l) => inSavedFolder(f, l, asked)).length]))
}

/**
 * WHY A CONDITION IS REFUSED — said in the dialog, before the save.
 *
 * A condition picked and left blank is the dangerous kind: "Lead source is one of (nothing)" holds
 * nobody, and one click on the other operator turns it into "is NOT one of (nothing)", which holds
 * EVERYBODY. A folder called "Only Zillow" showing all 110 conversations is worse than no folder.
 */
export function whyNotAsk(conditions = [], fieldById = () => null) {
  for (const c of conditions) {
    const def = fieldById(c.field)
    if (!def) return 'One of the conditions asks about something this product no longer has.'
    if (def.type === 'bool') continue
    const empty = c.value == null || c.value === ''
      || (Array.isArray(c.value) && (c.value.length === 0 || c.value.every((v) => v == null || v === '')))
    if (empty) return `Finish the condition on ${def.label.toLowerCase()}.`
  }
  return null
}


/**
 * WHY A NAME IS REFUSED, IN WORDS A PERSON CAN ACT ON — and refused here as well as by the database,
 * so the dialog can say it before the save rather than after the error.
 */
export function whyNotSaveable(f, others = []) {
  const name = text(f?.name, NAME_MAX)
  if (!name) return 'Give the folder a name.'
  const sameRail = others.filter((o) => o.id !== f.id && Boolean(o.shared) === Boolean(f.shared))
  if (sameRail.some((o) => o.name.toLowerCase() === name.toLowerCase())) {
    return f.shared ? `The team already has a folder called “${name}”.` : `You already have a folder called “${name}”.`
  }
  return null
}
